import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiCheckCircle } from 'react-icons/hi';
import { Modal, Button } from '@/components/common';
import { ReceiptVoucherResponse } from '@/types/receiptVoucher';
import { receiptVoucherApi, formatCurrency } from '@/api/endpoints/receiptVouchers';
import { toast } from '@/store/toastStore';

interface QuickConfirmReceiptVoucherModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmed: () => void;
  onSkipped: () => void;
  receiptVoucher: ReceiptVoucherResponse | null;
}

const QuickConfirmReceiptVoucherModal: React.FC<QuickConfirmReceiptVoucherModalProps> = ({
  isOpen,
  onClose,
  onConfirmed,
  onSkipped,
  receiptVoucher
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  if (!receiptVoucher) {
    return null;
  }

  const getCustomerName = (): string => {
    if (receiptVoucher.customer) {
      return receiptVoucher.customer.name || receiptVoucher.customer.code;
    }
    return receiptVoucher.customer_id || '-';
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await receiptVoucherApi.completeReceiptVoucher(receiptVoucher.code);
      toast.success(t('receiptVoucher.messages.completeSuccess'));
      onConfirmed();
      onClose();
    } catch (error: any) {
      console.error('Error completing receipt voucher:', error);
      toast.error(
        error.response?.data?.message || 
        t('receiptVoucher.messages.completeError')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    onSkipped();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleSkip}
      title={t('receiptVoucher.modals.quickConfirm.title', 'Xác nhận phiếu thu')}
      size="md"
    >
      <div className="p-6">
        {/* Success indicator */}
        <div className="flex items-center justify-center mb-4">
          <div className="flex items-center space-x-2 text-green-600">
            <HiCheckCircle className="w-8 h-8" />
            <span className="text-lg font-medium">
              {t('receiptVoucher.modals.quickConfirm.created', 'Tạo phiếu thu thành công!')}
            </span>
          </div>
        </div>

        <p className="text-gray-600 text-center mb-4">
          {t('receiptVoucher.modals.quickConfirm.message', 'Bạn có muốn xác nhận phiếu thu này ngay bây giờ?')}
        </p>
        
        {/* Voucher details */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-3 mb-6">
          <div className="flex justify-between">
            <span className="text-gray-600">{t('receiptVoucher.form.code', 'Mã phiếu')}:</span>
            <span className="font-medium">{receiptVoucher.code}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">{t('receiptVoucher.form.receiptType', 'Loại phiếu')}:</span>
            <span className="font-medium">{receiptVoucher.receipt_type_name || receiptVoucher.receipt_type}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">{t('receiptVoucher.list.customerName', 'Khách hàng')}:</span>
            <span className="font-medium">{getCustomerName()}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">{t('receiptVoucher.form.paymentMethod')}:</span>
            <span className="font-medium">
              {t(`receiptVoucher.paymentMethods.${receiptVoucher.payment_method}`)}
            </span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">{t('receiptVoucher.form.receivedNow')}:</span>
            <span className="font-medium text-lg text-green-600">
              +{formatCurrency(receiptVoucher.received_now)}
            </span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">{t('receiptVoucher.form.invoices', 'Hóa đơn')}:</span>
            <span className="font-medium">
              {receiptVoucher.invoice_ids.length} {t('receiptVoucher.list.invoicesCount', 'hóa đơn')}
            </span>
          </div>
          
          {receiptVoucher.note && (
            <div className="flex justify-between">
              <span className="text-gray-600">{t('receiptVoucher.form.note', 'Ghi chú')}:</span>
              <span className="font-medium text-right max-w-[200px] truncate">{receiptVoucher.note}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={handleSkip}
            disabled={loading}
          >
            {t('common.skip', 'Bỏ qua')}
          </Button>
          <Button
            variant="success"
            onClick={handleConfirm}
            loading={loading}
          >
            {t('receiptVoucher.actions.complete', 'Xác nhận')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default QuickConfirmReceiptVoucherModal;
